/**
 * WorkspaceSelect component provides a styled dropdown for workspace selection
 * with GraphQL integration for data fetching.
 *
 * Key features:
 * - Real-time workspace data fetching using GraphQL
 * - Custom styled dropdown using react-select
 * - Loading state handling with skeleton loader
 * - Error state fallback
 * - Network-only fetch policy to ensure data freshness
 */

import React, { FC, useMemo } from "react";
import { useQuery } from "@apollo/client";
import { Select } from "@foris/avocado-suite";
import { GET_WORKSPACES } from "../../graphql/context.queries";
import { useTranslation } from "react-i18next";
import { Icon } from "@foris/avocado-icons";
import styles from "./workspaceSelect.module.scss";

/**
 * Props interface for WorkspaceSelect component
 * @property {any} workspaceValue - Currently selected workspace value
 * @property {function} onChange - Callback function triggered when selection changes
 */
interface SelectProps {
  workspaceValue?: any;
  isClearable?: boolean;
  onChange: (value: any) => void;
}

/**
 * Renders a workspace selection dropdown with GraphQL integration
 * Handles loading and error states appropriately
 *
 * @param {SelectProps} props Component props
 * @param {any} props.workspaceValue Currently selected workspace
 * @param {function} props.onChange Callback for selection changes
 * @returns {JSX.Element} Styled select component or loading/error state
 */
const WorkspaceSelect: FC<SelectProps> = ({ workspaceValue, isClearable, onChange }) => {
  const { t } = useTranslation();

  // Fetch workspaces with network-only policy to ensure fresh data
  const { loading, error, data } = useQuery<any, any>(GET_WORKSPACES, {
    variables: {},
    fetchPolicy: "network-only",
  });

  // Transform workspace data into select options format
  const options = useMemo(
    () =>
      error || loading
        ? []
        : (data?.base?.workspaces ?? []).map((value: any) => {
            return { label: value.name, value: value.id };
          }),
    [data, error, loading],
  );

  return (
    <div className={styles.workspaceSelect}>
      <Select
        label={t("context-modal.workspace")}
        maxMenuHeight={150}
        options={options}
        isLoading={loading}
        placeholder={t("context-modal.select-workspace")}
        value={workspaceValue}
        onChange={(value: any): void => onChange(value)}
      />

      {!loading && !!workspaceValue && isClearable && (
        <button className={styles.workspaceSelect_clearBtn} onClick={() => onChange(null)}>
          <Icon name="close" size="sm" color="neutral-50" />
        </button>
      )}
    </div>
  );
};

export default WorkspaceSelect;
